import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import { API, authHeaders, money } from "./shared";

export default function CardFeeComplianceReport({ token }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/payments/card-fee-report`, authHeaders(token))
      .then((r) => setReport(r.data))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</p>;
  if (!report) return <p className="text-white/40 text-sm">No data.</p>;

  return (
    <div data-testid="card-fee-compliance-report" className="space-y-4">
      <div className="rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3.5 flex items-start gap-2.5">
        <ShieldAlert size={18} className="text-amber-300 mt-0.5 shrink-0" />
        <p className="text-amber-100/90 text-xs leading-relaxed" data-testid="prereq-unverified-banner">
          <span className="font-bold">Merchant prerequisites unverified:</span> card-network surcharge registration,
          state-law restrictions, and Stripe's own merchant-agreement approval for surcharging have NOT been
          confirmed. Verify with Stripe/your card processor and legal counsel before relying on this as fully compliant.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Fee Collected (kept)" value={money(report.totals.fee_collected)} />
        <Stat label="AutoHire Absorbed (cap)" value={money(report.totals.cost_absorbed_by_autohire)} warn />
        <Stat label="Actual Stripe Fee" value={money(report.totals.stripe_fee)} />
        <Stat label="Net Received" value={money(report.totals.net)} />
      </div>

      <div className="overflow-x-auto rounded-lg border border-white/10">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.04] text-white/45 uppercase text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Funding</th>
              <th className="text-right px-3 py-2">Base</th>
              <th className="text-right px-3 py-2">Fee Collected</th>
              <th className="text-right px-3 py-2">Capped?</th>
              <th className="text-right px-3 py-2">Absorbed</th>
              <th className="text-right px-3 py-2">Total Charged</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r, i) => (
              <tr key={i} className="border-t border-white/5" data-testid={`card-fee-row-${i}`}>
                <td className="px-3 py-2 text-white/50">{(r.at || "").slice(0, 10)}</td>
                <td className="px-3 py-2 text-white/75">{r.customer}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.fee_exempted_upfront ? "bg-green-500/15 text-green-300" : "bg-white/10 text-white/60"}`}>
                    {r.funding}{r.fee_exempted_upfront ? " — exempt" : ""}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-white/75">{money(r.base_payment)}</td>
                <td className="px-3 py-2 text-right text-white/75">{money(r.processing_fee_collected)}</td>
                <td className="px-3 py-2 text-right">{r.capped_at_3pct ? <AlertTriangle size={12} className="inline text-amber-400" /> : "—"}</td>
                <td className="px-3 py-2 text-right text-amber-300">{r.cost_absorbed_by_autohire > 0 ? money(r.cost_absorbed_by_autohire) : "—"}</td>
                <td className="px-3 py-2 text-right text-white font-semibold">{money(r.total_charged)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-white/35 text-[11px]">{report.note}</p>
    </div>
  );
}

const Stat = ({ label, value, warn }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
    <span className="block text-[10px] uppercase text-white/40">{label}</span>
    <span className={`text-lg font-bold ${warn ? "text-amber-300" : "text-white"}`}>{value}</span>
  </div>
);
